import React from 'react';
import { Alert, Button } from '@mui/material';
import { PermissionFailureClass } from './mePermissions';

/**
 * The visible half of `useRBAC().permissionsUnavailable` (P2 / INV-104).
 *
 * When `GET /users/me` cannot be read (it already retried once), the hook fails
 * CLOSED — permissions become `[]` and every gated affordance disappears. That is
 * the right security answer and the wrong user experience on its own: an empty
 * set renders exactly like "this account holds nothing", so a vet sees their
 * buttons gone and calls their admin about access they already have. This banner
 * is the difference between the two states, in the user's words.
 *
 * ⚠ It also covers the MILDER state: a transient failure that found an answer
 * already in the shared store keeps that answer (`mePermissions`'
 * `publishPermissionsUnavailable`), so nothing is hidden and the banner is only
 * saying "not re-checked". That is why the transient copy claims no consequence it
 * cannot verify — "some actions MAY be unavailable" is true in both states, where
 * "so some actions are hidden" was false in this one.
 *
 * Prop-driven, not a hook consumer: `AuthenticatedLayout` already calls
 * `useRBAC()`, and every extra call is another forced ID-token refresh on mount.
 *
 * Reload rather than a retry button: re-resolving one consumer is not a thing the
 * user can ask for — the shared store answers every `useRBAC` instance at once, and
 * a reload is the only action that makes it ask again from scratch.
 *
 * ⚠ THE WORDING CARRIES THE FAILURE CLASS, because the banner must not assert a
 * cause it does not know. "This is a connection problem" was printed for every
 * failure the transport declined to retry — a 403, the clinic-drift 401 that
 * survives a re-auth — where the sentence is false and Reload cannot help, leaving
 * the user in a fully broken app reading a reassurance. `'definitive'` therefore
 * names no cause, suggests the one thing that can help (a fresh sign-in), and
 * offers no button.
 */
export interface PermissionsUnavailableBannerProps {
  unavailable: boolean;
  /** From `useRBAC().permissionsFailure`. Absent/unknown reads as transient — the
   *  recoverable wording, which is also the pre-existing copy. */
  failure?: PermissionFailureClass | null;
  /** Injectable for tests; defaults to a real page reload. */
  onReload?: () => void;
}

export const PermissionsUnavailableBanner: React.FC<PermissionsUnavailableBannerProps> = ({
  unavailable,
  failure,
  onReload,
}) => {
  if (!unavailable) return null;
  if (failure === 'definitive') {
    return (
      <Alert severity="warning" sx={{ borderRadius: 0 }}>
        Your access could not be confirmed. Sign out and back in; if it persists, contact your
        clinic administrator.
      </Alert>
    );
  }
  return (
    <Alert
      severity="warning"
      sx={{ borderRadius: 0 }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={onReload ?? (() => window.location.reload())}
        >
          Reload
        </Button>
      }
    >
      We could not check your permissions just now — this is a connection problem, not a change to
      your access. Some actions may be unavailable until it succeeds.
    </Alert>
  );
};

export default PermissionsUnavailableBanner;
